import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../context/SettingsContext';
import type { Settings, MeasurementSystem } from '../context/SettingsContext';
import UsersModal from '../components/UsersModal';
import TwoFactorSection from '../components/TwoFactorSection';
import ProfileSection from '../components/ProfileSection';
import PaymentAccountsSection from '../components/PaymentAccountsSection';
import StripeConnectSection from '../components/StripeConnectSection';
import CalendarFeedSection from '../components/CalendarFeedSection';
import { getUsers, type AppUser } from '../api';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { settings, update } = useSettings();
  const [saved, setSaved] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [users, setUsers] = useState<AppUser[]>([]);

  useEffect(() => { getUsers().then(setUsers); }, []);

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    update({ [key]: value });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <main className="max-w-2xl mx-auto px-6 py-8">
      <h2 className="modal-title mb-6">{t('settings.title')}</h2>

      {/* Min profil */}
      <h3 className="section-title mt-0 mb-3">Min profil</h3>
      <div className="mb-6">
        <ProfileSection />
      </div>

      {/* Sikkerhed (2FA) */}
      <h3 className="section-title mb-3">Sikkerhed</h3>
      <div className="mb-6">
        <TwoFactorSection />
      </div>

      {/* Measurement system */}
      <h3 className="section-title mt-0 mb-3">{t('settings.measurement_system')}</h3>
      <div className="bg-white rounded-xl shadow-sm border divide-y mb-6">

        <Row label={t('settings.measurement_system_label')}>
          <select className="input w-48" value={settings.measurementSystem}
            onChange={e => set('measurementSystem', e.target.value as MeasurementSystem)}>
            <option value="metric">Metric (EU/DK)</option>
            <option value="imperial_us">Imperial US</option>
            <option value="imperial_uk">Imperial UK</option>
          </select>
        </Row>

        <Row label={t('settings.pressure_unit')}>
          <select className="input w-48" value={settings.pressureUnit}
            onChange={e => set('pressureUnit', e.target.value as Settings['pressureUnit'])}>
            <option value="bar">bar</option>
            <option value="psi">psi</option>
            <option value="kpa">kPa</option>
          </select>
        </Row>

        <Row label={t('settings.length_unit')}>
          <select className="input w-48" value={settings.lengthUnit}
            onChange={e => set('lengthUnit', e.target.value as Settings['lengthUnit'])}>
            <option value="mm">mm</option>
            <option value="inch">inch (in)</option>
          </select>
        </Row>

      </div>

      <div className="bg-white rounded-xl shadow-sm border divide-y">

        <Row label={t('settings.language')}>
          <select className="input w-48" value={settings.language}
            onChange={e => set('language', e.target.value as Settings['language'])}>
            <option value="da">Dansk</option>
            <option value="en">English</option>
          </select>
        </Row>

        <Row label={t('settings.timezone')}>
          <select className="input w-72" value={settings.timezone}
            onChange={e => set('timezone', e.target.value)}>
            <option value="Etc/GMT+12">GMT-12 — Baker Island, Howland Island</option>
            <option value="Etc/GMT+11">GMT-11 — Niue, American Samoa</option>
            <option value="Pacific/Honolulu">GMT-10 — Hawaii</option>
            <option value="Pacific/Marquesas">GMT-9:30 — Marquesas Islands</option>
            <option value="America/Anchorage">GMT-9 — Alaska</option>
            <option value="America/Los_Angeles">GMT-8 — Los Angeles, Vancouver</option>
            <option value="America/Denver">GMT-7 — Denver, Phoenix</option>
            <option value="America/Chicago">GMT-6 — Chicago, Mexico City</option>
            <option value="America/New_York">GMT-5 — New York, Toronto, Lima</option>
            <option value="America/Caracas">GMT-4:30 — Caracas</option>
            <option value="America/Halifax">GMT-4 — Halifax, Santiago</option>
            <option value="America/St_Johns">GMT-3:30 — Newfoundland</option>
            <option value="America/Sao_Paulo">GMT-3 — São Paulo, Buenos Aires</option>
            <option value="Atlantic/South_Georgia">GMT-2 — South Georgia</option>
            <option value="Atlantic/Azores">GMT-1 — Azores, Cape Verde</option>
            <option value="UTC">GMT+0 — UTC, London, Reykjavik</option>
            <option value="Europe/Copenhagen">GMT+1 — København, Oslo, Stockholm</option>
            <option value="Europe/Paris">GMT+1 — Paris, Berlin, Rome, Madrid</option>
            <option value="Europe/Helsinki">GMT+2 — Helsinki, Athens, Cairo, Kyiv</option>
            <option value="Europe/Moscow">GMT+3 — Moscow, Nairobi, Riyadh</option>
            <option value="Asia/Tehran">GMT+3:30 — Tehran</option>
            <option value="Asia/Dubai">GMT+4 — Dubai, Baku, Tbilisi</option>
            <option value="Asia/Kabul">GMT+4:30 — Kabul</option>
            <option value="Asia/Karachi">GMT+5 — Karachi, Tashkent</option>
            <option value="Asia/Kolkata">GMT+5:30 — Mumbai, Kolkata, Colombo</option>
            <option value="Asia/Kathmandu">GMT+5:45 — Kathmandu</option>
            <option value="Asia/Dhaka">GMT+6 — Dhaka, Almaty</option>
            <option value="Asia/Yangon">GMT+6:30 — Yangon</option>
            <option value="Asia/Bangkok">GMT+7 — Bangkok, Jakarta, Ho Chi Minh</option>
            <option value="Asia/Shanghai">GMT+8 — Shanghai, Singapore, Perth</option>
            <option value="Australia/Eucla">GMT+8:45 — Eucla</option>
            <option value="Asia/Tokyo">GMT+9 — Tokyo, Seoul</option>
            <option value="Australia/Darwin">GMT+9:30 — Darwin, Adelaide</option>
            <option value="Australia/Sydney">GMT+10 — Sydney, Brisbane, Guam</option>
            <option value="Australia/Lord_Howe">GMT+10:30 — Lord Howe Island</option>
            <option value="Pacific/Noumea">GMT+11 — Noumea, Guadalcanal</option>
            <option value="Pacific/Auckland">GMT+12 — Auckland, Fiji</option>
            <option value="Pacific/Chatham">GMT+12:45 — Chatham Islands</option>
            <option value="Pacific/Apia">GMT+13 — Apia, Tongatapu</option>
            <option value="Pacific/Kiritimati">GMT+14 — Kiritimati</option>
          </select>
        </Row>

        <Row label={t('settings.date_format')}>
          <select className="input w-48" value={settings.dateFormat}
            onChange={e => set('dateFormat', e.target.value as Settings['dateFormat'])}>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </Row>

        <Row label={t('settings.distance_unit')}>
          <select className="input w-48" value={settings.distanceUnit}
            onChange={e => set('distanceUnit', e.target.value as Settings['distanceUnit'])}>
            <option value="km">Kilometers (km)</option>
            <option value="miles">Miles</option>
          </select>
        </Row>

        <Row label={t('settings.fuel_unit')}>
          <select className="input w-48" value={settings.fuelUnit}
            onChange={e => set('fuelUnit', e.target.value as Settings['fuelUnit'])}>
            <option value="liters">Liter (L)</option>
            <option value="gallons_us">Gallons — US (3,785 L)</option>
            <option value="gallons_uk">Gallons — UK (4,546 L)</option>
          </select>
        </Row>

        <Row label={t('settings.currency')}>
          <select className="input w-48" value={settings.currency}
            onChange={e => set('currency', e.target.value as Settings['currency'])}>
            <option value="DKK">DKK — Danish Krone</option>
            <option value="EUR">EUR — Euro</option>
            <option value="GBP">GBP — British Pound</option>
            <option value="USD">USD — US Dollar</option>
          </select>
        </Row>

        <Row label={t('settings.items_per_page')}>
          <select className="input w-48" value={settings.itemsPerPage}
            onChange={e => set('itemsPerPage', +e.target.value as Settings['itemsPerPage'])}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </Row>

        <Row label={t('settings.vat_rate')}>
          <div className="flex items-center gap-2">
            <input type="number" min={0} max={100} step={0.1} className="input w-24 text-right"
              value={settings.vatRate}
              onChange={e => set('vatRate', +e.target.value)} />
            <span className="text-sm text-gray-500">%</span>
          </div>
        </Row>

        <Row label={t('settings.number_format')}>
          <select className="input w-48" value={settings.numberFormat}
            onChange={e => set('numberFormat', e.target.value as Settings['numberFormat'])}>
            <option value="dk">90.254,50 (DK)</option>
            <option value="us">90,254.50 (US/UK)</option>
          </select>
        </Row>

      </div>

      {/* Inspection rules */}
      <h3 className="section-title mt-8 mb-3">{t('settings.inspection_rules')}</h3>
      <div className="bg-white rounded-xl shadow-sm border divide-y">

        <Row label={t('settings.inspection_name')}>
          <input className="input w-48" value={settings.inspectionName}
            onChange={e => set('inspectionName', e.target.value)} />
        </Row>

        <Row label={t('settings.first_inspection_age')}>
          <input type="number" min={1} max={20} className="input w-48" value={settings.firstInspectionAge}
            onChange={e => set('firstInspectionAge', +e.target.value)} />
        </Row>

        <Row label={t('settings.inspection_interval')}>
          <input type="number" min={1} max={10} className="input w-48" value={settings.inspectionInterval}
            onChange={e => set('inspectionInterval', +e.target.value)} />
        </Row>

        <Row label={t('settings.inspection_reminder_days')}>
          <input type="number" min={1} max={365} className="input w-48" value={settings.inspectionReminderDays}
            onChange={e => set('inspectionReminderDays', +e.target.value)} />
        </Row>

        <Row label={t('settings.inspection_reminder_km')}>
          <div className="flex items-center gap-2">
            <input type="number" min={0} step={100} className="input w-32 text-right" value={settings.inspectionReminderKm}
              onChange={e => set('inspectionReminderKm', +e.target.value)} />
            <span className="text-sm text-gray-500">{settings.distanceUnit === 'miles' ? 'mi' : 'km'}</span>
          </div>
        </Row>

      </div>

      {/* Integrationer */}
      <PaymentAccountsSection />

      <h3 className="section-title mt-8 mb-3">Direct booking — Stripe</h3>
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <StripeConnectSection />
      </div>

      <h3 className="section-title mt-8 mb-3">Integrationer</h3>
      <div className="bg-white rounded-xl shadow-sm border divide-y mb-6">
        <div className="px-6 py-4 space-y-1">
          <label className="text-sm font-medium text-gray-700 block">Eksternt kalender feed (iCal-URL)</label>
          <p className="text-xs text-gray-400">F.eks. GoMore: find linket under din profil → Kalender → Kopiér iCal-link</p>
          <input
            type="url"
            className="input w-full mt-1"
            value={settings.gomore_ical_url}
            onChange={e => set('gomore_ical_url', e.target.value)}
            placeholder="https://gomore.dk/accounts/calendar/..."
          />
        </div>
        <CalendarFeedSection />
      </div>

      {/* Users */}
      <h3 className="section-title mt-8 mb-3">{t('users.title')}</h3>
      <div className="bg-white rounded-xl shadow-sm border divide-y">
        <Row label={t('settings.default_user')}>
          <select className="input w-48" value={settings.defaultUser}
            onChange={e => set('defaultUser', e.target.value)}>
            <option value="">— {t('settings.default_user_none')} —</option>
            {users.map(u => (
              <option key={u.id} value={u.name}>{u.name}</option>
            ))}
          </select>
        </Row>
        <div className="flex items-center justify-between px-6 py-4">
          <span className="text-sm font-medium text-gray-700">{t('users.description')}</span>
          <button onClick={() => { setShowUsers(true); getUsers().then(setUsers); }} className="btn btn-secondary">{t('users.manage')}</button>
        </div>
      </div>

      {saved && (
        <p className="mt-4 text-sm text-[var(--color-success)] font-medium">{t('settings.saved')}</p>
      )}

      {showUsers && <UsersModal onClose={() => setShowUsers(false)} />}
    </main>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-6 py-4">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {children}
    </div>
  );
}
